export type BlogSection = {
  heading: string;
  paragraphs: string[];
  points?: string[];
};

export type StaticBlogArticle = {
  title: string;
  slug: string;
  excerpt: string;
  coverImage: string;
  author: string;
  updatedAt: string;
  readingTime: string;
  sections: BlogSection[];
};

export const staticBlogArticles: StaticBlogArticle[] = [
  {
    title: "How to Build a Private-Label Product Brief That a Formulation Lab Can Use",
    slug: "private-label-product-brief-checklist",
    excerpt: "A practical checklist for turning a brand idea into a testable formulation brief, sample plan, packaging direction, and realistic first production batch.",
    coverImage: "/assets/categories/skin-care-v2.jpg",
    author: "Shaaira Biocare R&D Team",
    updatedAt: "2026-08-27",
    readingTime: "6 min read",
    sections: [
      {
        heading: "Start with the consumer problem, not an ingredient list",
        paragraphs: [
          "A useful product brief explains who the formula is for, when they will use it, the result they expect, and which benchmark experience the product should match. An ingredient wish list without that context leaves the chemist guessing about texture, tolerance, pack choice, and cost.",
          "For example, a lightweight niacinamide serum for humid climates is a clearer direction than a request for a premium glow serum. The first direction helps the lab choose a viscosity range, humectant system, finish, and suitable preservative approach.",
        ],
        points: [
          "Target user and skin or hair concern",
          "Day, night, rinse-off, or leave-on use",
          "Benchmark product and the qualities you want to retain",
          "Target retail price and expected pack size",
        ],
      },
      {
        heading: "Separate fixed requirements from preferences",
        paragraphs: [
          "Mark non-negotiable requirements clearly: excluded ingredients, required active levels, fragrance restrictions, claim territory, target market, and packaging limitations. Keep sensory preferences such as slip, absorption speed, foam, and fragrance profile in a separate section so the lab can propose trade-offs.",
        ],
        points: [
          "Required and excluded ingredients",
          "Target pH or active concentration where justified",
          "Vegan, fragrance-free, or market-specific constraints",
          "Preferred texture, colour, finish, and fragrance direction",
        ],
      },
      {
        heading: "Plan sampling and commercial decisions together",
        paragraphs: [
          "A sample is only useful when the evaluation criteria are agreed in advance. Record feedback on spread, absorption, residue, fragrance, dispensing, and pack compatibility. Once the formula direction is approved, confirm the production quantity, component lead time, artwork, testing scope, and documentation before booking the batch.",
        ],
      },
    ],
  },
  {
    title: "What a Cosmetic Batch CoA Should Tell Your Brand",
    slug: "cosmetic-batch-coa-guide",
    excerpt: "Understand the role of a Certificate of Analysis, the checks commonly recorded for cosmetic batches, and the questions to ask before dispatch.",
    coverImage: "/og-lab-v2.jpg",
    author: "Shaaira Biocare Quality Team",
    updatedAt: "2026-08-27",
    readingTime: "5 min read",
    sections: [
      {
        heading: "A CoA connects the approved specification to the manufactured batch",
        paragraphs: [
          "A Certificate of Analysis records whether a specific production batch met its agreed release specification. It should identify the product, batch number, manufacturing or test date, test method or parameter, acceptance range, observed result, and release authorization.",
          "The document is not a substitute for a complete product safety assessment or stability programme. It is one part of the batch record that helps a brand trace what was manufactured and why it was released.",
        ],
      },
      {
        heading: "Common cosmetic release checks",
        paragraphs: [
          "The exact panel depends on product type, claims, ingredients, pack, and market. A water-based serum and an anhydrous balm should not be released against an identical checklist.",
        ],
        points: [
          "Appearance, colour, odour, and texture",
          "pH, viscosity, and specific gravity where relevant",
          "Fill weight or volume and packaging checks",
          "Microbial limits appropriate to the product category",
          "Any agreed active or marker test supported by a validated method",
        ],
      },
      {
        heading: "Questions to resolve before the first production batch",
        paragraphs: [
          "Ask who owns the finished-product specification, which results appear on the CoA, how out-of-specification results are handled, how retains are stored, and how long records remain available. Agreeing these points during development prevents documentation gaps at dispatch.",
        ],
      },
    ],
  },
  {
    title: "Choosing Packaging for Air-Sensitive Cosmetic Actives",
    slug: "packaging-air-sensitive-cosmetic-actives",
    excerpt: "A formulation-first guide to evaluating airless pumps, droppers, opaque packs, and compatibility testing for oxidation-sensitive products.",
    coverImage: "/assets/categories/kits-v2.jpg",
    author: "Shaaira Biocare Packaging Team",
    updatedAt: "2026-08-27",
    readingTime: "7 min read",
    sections: [
      {
        heading: "Packaging is part of the formulation system",
        paragraphs: [
          "A stable bulk formula can still fail in market if the pack introduces too much air, light, contamination risk, evaporation, or material interaction. Package selection should begin while the formula is being developed, not after the final sample is approved.",
        ],
      },
      {
        heading: "Match the dispensing system to the risk",
        paragraphs: [
          "Airless pumps can reduce repeated air exchange and direct finger contact. Opaque or UV-limiting packs may help when light exposure is a known risk. Droppers provide a familiar premium experience but expose the headspace whenever the pack is opened and require careful compatibility review.",
        ],
        points: [
          "Oxygen and light sensitivity of the formula",
          "Viscosity and dose accuracy",
          "Consumer handling and contamination exposure",
          "Component material, seals, springs, and decoration",
          "Target fill volume, shipping conditions, and e-commerce leakage risk",
        ],
      },
      {
        heading: "Run compatibility checks with the production-intent pack",
        paragraphs: [
          "Compatibility work should use the intended component set and filled product. Observe dispensing, leakage, paneling, staining, odour change, corrosion, label adhesion, and visible formula change under the agreed storage conditions. A visually similar sample pack is not proof that the final sourced component will behave the same way.",
        ],
      },
    ],
  },
];

export const getStaticBlogArticle = (slug: string) =>
  staticBlogArticles.find((article) => article.slug === slug);
